/**
 * NotificationBell Component
 * Displays a bell icon with badge for pending invitations and notifications
 * Shows dropdown with actionable notifications for both Household and Envelope Challenges
 */
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, Users, Check, X } from 'lucide-react';
import { apiGet, apiPost } from '@/utils/api';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

export function NotificationBell({ user }) {
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const wsRef = useRef(null);

  // Fetch pending invitations (both household and envelope challenges)
  const fetchNotifications = async () => {
    try {
      const response = await apiGet('/notifications/pending');
      if (response.ok) {
        const data = await response.json();
        setNotifications(data.invitations || []);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  // Set up WebSocket for real-time notifications
  useEffect(() => {
    if (!user) return;

    const connectWebSocket = () => {
      const ws = new WebSocket(`${WS_URL}/api/notifications/ws/${user.user_id}`);
      
      ws.onopen = () => {
        console.log('Notification WebSocket connected');
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'new_invitation') {
            // Household invitation
            setNotifications(prev => [data.invitation, ...prev]);
            toast.info(`${data.invitation.sender_name} invited you to join "${data.invitation.household_name}"`);
          } else if (data.type === 'new_challenge_invite') {
            // Envelope challenge invitation
            const invite = data.invitation;
            setNotifications(prev => [{
              ...invite,
              type: 'envelope_challenge'
            }, ...prev]);
            toast.info(`${invite.sender_name} invited you to the "${invite.challenge_name}" savings challenge! 📨`);
          } else if (data.type === 'invitation_cancelled') {
            setNotifications(prev => prev.filter(n => n.id !== data.invite_id));
          }
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };

      ws.onclose = () => {
        console.log('Notification WebSocket closed, reconnecting...');
        setTimeout(connectWebSocket, 3000);
      };

      ws.onerror = (error) => {
        console.error('Notification WebSocket error:', error);
      };

      wsRef.current = ws;
    };

    connectWebSocket();
    fetchNotifications();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [user]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle accept invitation
  const handleAccept = async (invite) => {
    setLoading(true);
    try {
      // Determine the correct endpoint based on invite type
      const endpoint = invite.type === 'envelope_challenge' 
        ? `/notifications/challenge-invitations/${invite.id}/accept`
        : `/notifications/invitations/${invite.id}/accept`;
      
      const response = await apiPost(endpoint);
      if (response.ok) {
        const data = await response.json();
        
        if (invite.type === 'envelope_challenge') {
          toast.success(`You've joined "${invite.challenge_name}"! 📨`);
          setNotifications(prev => prev.filter(n => n.id !== invite.id));
          // Redirect to envelope challenge page
          window.location.href = '/app/envelope-v2';
        } else {
          toast.success(`You've joined "${invite.household_name}"!`);
          setNotifications(prev => prev.filter(n => n.id !== invite.id));
          // Redirect to household page
          window.location.href = '/app/household';
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to accept invitation');
      }
    } catch (error) {
      toast.error('Error accepting invitation');
    } finally {
      setLoading(false);
    }
  };

  // Handle decline invitation
  const handleDecline = async (invite) => {
    setLoading(true);
    try {
      // Determine the correct endpoint based on invite type
      const endpoint = invite.type === 'envelope_challenge' 
        ? `/notifications/challenge-invitations/${invite.id}/decline`
        : `/notifications/invitations/${invite.id}/decline`;
      
      const response = await apiPost(endpoint);
      if (response.ok) {
        toast.info('Invitation declined');
        setNotifications(prev => prev.filter(n => n.id !== invite.id));
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to decline invitation');
      }
    } catch (error) {
      toast.error('Error declining invitation');
    } finally {
      setLoading(false);
    }
  };

  // Handle dismiss reminder
  const handleDismissReminder = async (reminder) => {
    setLoading(true);
    try {
      const response = await apiPost(`/notifications/reminders/${reminder.id}/dismiss`);
      if (response.ok) {
        setNotifications(prev => prev.filter(n => n.id !== reminder.id));
      }
    } catch (error) {
      console.error('Error dismissing reminder:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle go to challenge from reminder
  const handleGoToChallenge = async (reminder) => {
    // Dismiss the reminder
    try {
      await apiPost(`/notifications/reminders/${reminder.id}/dismiss`);
    } catch (e) {
      // Continue anyway
    }
    setNotifications(prev => prev.filter(n => n.id !== reminder.id));
    window.location.href = '/app/envelope-v2';
  };

  // Get display name and icon based on notification type
  const getInviteDisplay = (invite) => {
    if (invite.type === 'envelope_reminder') {
      return {
        name: invite.challenge_name,
        icon: '⏰',
        iconBg: 'bg-orange-100',
        iconColor: 'text-orange-600',
        label: 'Daily Reminder',
        goal: `Day ${invite.current_day}/${invite.duration}`,
        isReminder: true
      };
    }
    if (invite.type === 'envelope_challenge') {
      return {
        name: invite.challenge_name,
        icon: '📨',
        iconBg: 'bg-amber-100',
        iconColor: 'text-amber-600',
        label: 'Savings Challenge',
        goal: invite.challenge_goal ? `$${invite.challenge_goal} goal` : null,
        isReminder: false
      };
    }
    return {
      name: invite.household_name,
      icon: null,
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600',
      label: 'Household Budget',
      goal: null,
      isReminder: false
    };
  };

  const unreadCount = notifications.length;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-full transition-colors"
        data-testid="notification-bell"
        aria-label={`Notifications ${unreadCount > 0 ? `(${unreadCount} unread)` : ''}`}
      >
        <Bell className="w-5 h-5" />
        
        {/* Badge */}
        <AnimatePresence>
          {unreadCount > 0 && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </motion.span>
          )}
        </AnimatePresence>
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden z-50"
          >
            {/* Header */}
            <div className="px-4 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
              <h3 className="font-semibold flex items-center gap-2">
                <Bell className="w-4 h-4" />
                Notifications
              </h3>
            </div>

            {/* Notification List */}
            <div className="max-h-96 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  <Bell className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No new notifications</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {notifications.map((invite) => {
                    const display = getInviteDisplay(invite);
                    return (
                      <motion.div
                        key={invite.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="p-4 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-start gap-3">
                          {/* Icon */}
                          <div className={`w-10 h-10 rounded-full ${display.iconBg} flex items-center justify-center flex-shrink-0`}>
                            {display.icon ? (
                              <span className="text-xl">{display.icon}</span>
                            ) : (
                              <Users className={`w-5 h-5 ${display.iconColor}`} />
                            )}
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            {display.isReminder ? (
                              <>
                                <p className="text-sm text-gray-900 font-medium">
                                  {invite.message || `Time to check your "${display.name}" challenge!`}
                                </p>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">
                                    {display.label}
                                  </span>
                                  {display.goal && (
                                    <span className="text-xs text-gray-500">{display.goal}</span>
                                  )}
                                </div>
                                <p className="text-xs text-gray-400 mt-1">
                                  {new Date(invite.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                </p>

                                {/* Reminder Action Buttons */}
                                <div className="flex gap-2 mt-3">
                                  <button
                                    onClick={() => handleGoToChallenge(invite)}
                                    disabled={loading}
                                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-amber-500 text-white text-sm font-medium rounded-lg hover:bg-amber-600 disabled:opacity-50 transition-colors"
                                  >
                                    📨 Open Challenge
                                  </button>
                                  <button
                                    onClick={() => handleDismissReminder(invite)}
                                    disabled={loading}
                                    className="px-3 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-300 disabled:opacity-50 transition-colors"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              </>
                            ) : (
                              <>
                                <p className="text-sm text-gray-900">
                                  <span className="font-semibold">{invite.sender_name}</span>
                                  {' '}invited you to join
                                </p>
                                <p className={`text-sm font-medium truncate ${invite.type === 'envelope_challenge' ? 'text-amber-600' : 'text-purple-600'}`}>
                                  "{display.name}"
                                </p>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${invite.type === 'envelope_challenge' ? 'bg-amber-100 text-amber-700' : 'bg-purple-100 text-purple-700'}`}>
                                    {display.label}
                                  </span>
                                  {display.goal && (
                                    <span className="text-xs text-gray-500">{display.goal}</span>
                                  )}
                                </div>
                                <p className="text-xs text-gray-400 mt-1">
                                  {new Date(invite.created_at).toLocaleDateString()}
                                </p>

                                {/* Invitation Action Buttons */}
                                <div className="flex gap-2 mt-3">
                                  <button
                                    onClick={() => handleAccept(invite)}
                                    disabled={loading}
                                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-emerald-500 text-white text-sm font-medium rounded-lg hover:bg-emerald-600 disabled:opacity-50 transition-colors"
                                    data-testid={`accept-invite-${invite.id}`}
                                  >
                                    <Check className="w-4 h-4" />
                                    Accept
                                  </button>
                                  <button
                                    onClick={() => handleDecline(invite)}
                                    disabled={loading}
                                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-300 disabled:opacity-50 transition-colors"
                                    data-testid={`decline-invite-${invite.id}`}
                                  >
                                    <X className="w-4 h-4" />
                                    Decline
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * InvitationModal Component
 * Full-screen modal that appears when user logs in with pending invitations
 */
export function InvitationModal({ invitation, onAccept, onDecline, onClose }) {
  const [loading, setLoading] = useState(false);

  const handleAccept = async () => {
    setLoading(true);
    try {
      const response = await apiPost(`/notifications/invitations/${invitation.id}/accept`);
      if (response.ok) {
        toast.success(`You've joined "${invitation.household_name}"!`);
        onAccept();
        window.location.href = '/app/household';
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to accept invitation');
      }
    } catch (error) {
      toast.error('Error accepting invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleDecline = async () => {
    setLoading(true);
    try {
      const response = await apiPost(`/notifications/invitations/${invitation.id}/decline`);
      if (response.ok) {
        toast.info('Invitation declined');
        onDecline();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to decline invitation');
      }
    } catch (error) {
      toast.error('Error declining invitation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        className="bg-white rounded-3xl p-6 w-full max-w-md shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header with icon */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
            <Users className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-xl font-bold text-gray-900">You're Invited!</h2>
        </div>

        {/* Invitation Details */}
        <div className="bg-gray-50 rounded-2xl p-4 mb-6">
          <p className="text-gray-600 text-center">
            <span className="font-semibold text-gray-900">{invitation.sender_name}</span>
            {' '}has invited you to join their Household Budget:
          </p>
          <p className="text-lg font-bold text-purple-600 text-center mt-2">
            "{invitation.household_name}"
          </p>
        </div>

        {/* Description */}
        <p className="text-sm text-gray-500 text-center mb-6">
          Join to share bills, track contributions, and manage your household finances together!
        </p>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={handleDecline}
            disabled={loading}
            className="flex-1 py-3 px-4 border-2 border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 disabled:opacity-50 transition-colors"
            data-testid="modal-decline-invite"
          >
            Decline
          </button>
          <button
            onClick={handleAccept}
            disabled={loading}
            className="flex-1 py-3 px-4 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-semibold rounded-xl hover:from-emerald-600 hover:to-emerald-700 disabled:opacity-50 transition-all shadow-lg shadow-emerald-500/30"
            data-testid="modal-accept-invite"
          >
            {loading ? 'Joining...' : 'Accept & Join'}
          </button>
        </div>

        {/* Later button */}
        <button
          onClick={onClose}
          className="w-full mt-3 py-2 text-sm text-gray-400 hover:text-gray-600 transition-colors"
        >
          I'll decide later
        </button>
      </motion.div>
    </motion.div>
  );
}

/**
 * UserNotFoundModal Component
 * Shows when trying to invite a user who doesn't have GuapGoals
 */
export function UserNotFoundModal({ email, onClose, householdName }) {
  const shareMessage = `Hey! I'm setting up our Household Budget on GuapGoals. Download the app and let's get our money right! 💰`;
  const appStoreLink = 'https://guapgoals.com'; // Replace with actual app store link

  // Check if Web Share API is available
  const canShare = typeof navigator !== 'undefined' && navigator.share;

  // Handle native share (for mobile with Web Share API)
  const handleNativeShare = async () => {
    try {
      await navigator.share({
        title: 'Join me on GuapGoals!',
        text: shareMessage,
        url: appStoreLink
      });
      toast.success('Shared successfully!');
      onClose();
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Share failed:', error);
      }
    }
  };

  // Handle SMS share
  const handleSendText = () => {
    const smsBody = encodeURIComponent(`${shareMessage} ${appStoreLink}`);
    // sms: works on iOS and Android
    window.open(`sms:?body=${smsBody}`, '_blank');
    onClose();
  };

  // Handle Email share
  const handleSendEmail = () => {
    const subject = encodeURIComponent(`Join me on GuapGoals - ${householdName}`);
    const body = encodeURIComponent(`${shareMessage}\n\n${appStoreLink}`);
    window.open(`mailto:${email}?subject=${subject}&body=${body}`, '_blank');
    onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        className="bg-white rounded-3xl p-6 w-full max-w-md shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg">
            <span className="text-3xl">🔍</span>
          </div>
          <h2 className="text-xl font-bold text-gray-900">User Not Found</h2>
        </div>

        {/* Message */}
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6">
          <p className="text-amber-800 text-center">
            Looks like <span className="font-semibold">{email}</span> isn't on GuapGoals yet!
          </p>
        </div>

        <p className="text-sm text-gray-500 text-center mb-6">
          Would you like to send them an invite to download the app and join your household?
        </p>

        {/* Share Buttons */}
        <div className="space-y-3">
          {canShare && (
            <button
              onClick={handleNativeShare}
              className="w-full py-3 px-4 bg-gradient-to-r from-purple-500 to-indigo-600 text-white font-semibold rounded-xl hover:from-purple-600 hover:to-indigo-700 transition-all flex items-center justify-center gap-2"
            >
              <span>📤</span> Share Invite Link
            </button>
          )}

          <div className="flex gap-3">
            <button
              onClick={handleSendText}
              className="flex-1 py-3 px-4 bg-emerald-500 text-white font-semibold rounded-xl hover:bg-emerald-600 transition-colors flex items-center justify-center gap-2"
              data-testid="send-text-invite"
            >
              <span>💬</span> Send Text
            </button>
            <button
              onClick={handleSendEmail}
              className="flex-1 py-3 px-4 bg-blue-500 text-white font-semibold rounded-xl hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
              data-testid="send-email-invite"
            >
              <span>📧</span> Send Email
            </button>
          </div>
        </div>

        {/* Cancel button */}
        <button
          onClick={onClose}
          className="w-full mt-4 py-2 text-sm text-gray-400 hover:text-gray-600 transition-colors"
        >
          Cancel
        </button>
      </motion.div>
    </motion.div>
  );
}

export default NotificationBell;
